import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Paper, Tooltip, IconButton, Typography } from '@material-ui/core';
import { Refresh, Delete } from '@material-ui/icons';
import { hasPermission, hasAtLeastOnePermission, localization, getDimensionsOfPagination, parseURL } from '../sc-utilities';
import OEMPartDelete from './OEMPartDelete';
import * as actions from './actions';
import * as action from '../np-core/Auth/store/authActions';
import Toolbar from '../sc-utilities/Toolbar';
import { MuiTableRows } from '../../themes';
import { CancelToken } from 'axios';

const styles = theme => ({
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const OEMPartList = props => {
    const { classes, onFetchOEMParts, onFetchOEMPartsCount, tableColumns, onBulkDeleteOEMParts, saveColumnsFilter, activeCustomerID, translations, permissions, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, oemPartIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['oem_part_delete']);
    const deletePermission = hasPermission(permissions, 'oem_part_delete');

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        selectionProps: rowData => {
            let disabled = false;
            if (!deletePermission) {
                disabled = true;
            }
            rowData.tableData.disabled = disabled;
            return disabled? {
                disabled: true,
                id: `checkbox_${rowData.OEMPartID}`,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - 330 < 300 ? 300 : window.innerHeight - 330,
        maxBodyHeight: window.innerHeight - 330,
        pageSizeOptions: rowsPageSizeInit,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        const searchParams = parseURL(window.location.search);
        q['ProductID'] = searchParams.productID;
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchOEMPartsCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await onFetchOEMParts(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                <Typography className={classes.typography}>{translations['oem_parts'] || 'OEM Parts'}</Typography>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, oemPartIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteOEMParts({ OEMPartIDs: deleteModalState.oemPartIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, oemPartIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = oemPartIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, oemPartIDs: oemPartIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["oem_parts"] || "OEM Parts"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(oemPart => oemPart.OEMPartID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`OEMPart`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                actions={tableActions}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'oem_part_delete') ? (
                    <OEMPartDelete
                        entityName={translations['oem_part'] || 'OEM Part'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        deleteModalState={deleteModalState}
                    />
                ) : null
            }
        </Paper>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || []
    };
}

const mapDispatchToProps = dispatch => {
    return {
        onFetchOEMPartsCount: (filterParams, options) => dispatch(actions.fetchOEMPartCount(filterParams, options)),
        onFetchOEMParts: (filterParams, options) => dispatch(actions.fetchOEMPart(filterParams, options)),
        onBulkDeleteOEMParts: oemPart => dispatch(actions.bulkDeleteOEMParts(oemPart)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(OEMPartList));